import React, { useContext, useEffect, useState } from 'react';
import { Formik } from 'formik';
import * as Yup from 'yup';
import { CButton, CCard, CCol, CForm, CLink, CModal, CModalBody, CRow, CSpinner } from '@coreui/react';
import CFormGroup from '../../../../../migration/CFormGroup';
import CLabel from '../../../../../migration/CLabel';
import { CInput, CTextarea } from '../../../../../migration/CInput';
import { CInvalidFeedback } from '../../../../../migration/CInvalidFeedback';
import Select, { components } from 'react-select';
import { ADD_USER, CLIENT_SCHEDULE_SAVED_REPORT } from '../../../../../../constants';
import { useDispatch, useSelector } from 'react-redux';
import {
     fetchAccountUsers,
     setManagers,
     setSavedReport,
     setScheduleSavedReport,
     setShowBlockAccountPopup,
     toggleAsideLimitationHit,
} from '../../../../../../actions/subscriber';
import { toastError } from '../../../../../../utils';
import { callCompressTokenApi, callTokenApi } from '../../../../../../apiCaller';
import { ScheduleContext } from '..';
import { toast } from 'react-toastify';
import CIcon from '@coreui/icons-react';
import { setAsideShow } from '../../../../../../actions/common';
import { InviteUserPopup } from '../../../../../general/popup';

const validationSchema = (schedules) => {
     return Yup.object().shape({
          name: Yup.string()
               .required('Schedule name is required')
               .max(200, `Name contains at most 200 characters`)
               .test('unique-name', 'This name already exists in the schedules', (value) => {
                    return !schedules.some((schedule) => schedule.name === value);
               }),
          emailList: Yup.array().required('Recipients are required'),
          customSubject: Yup.string().required('Custom subject is required'),
          optionalMessage: Yup.string().nullable(),
          schedule: Yup.string().required('Schedule type is required'),
          specificDay: Yup.string().when('schedule', {
               is: (schedule) => schedule === 'monthly' || schedule === 'quarterly',
               then: Yup.string().required('Specific day is required'),
               otherwise: Yup.string().notRequired(),
          }),
          daysOfWeek: Yup.array().when('schedule', {
               is: 'weekly',
               then: Yup.array().required('Days of the week are required'),
               otherwise: Yup.array().notRequired(),
          }),
     });
};

const CreateSchedule = ({ savedReports, showCreate, setShowCreate }) => {
     const { reportData } = useContext(ScheduleContext);
     const dispatch = useDispatch();
     const owner = useSelector((state) => state.subscriber.owner);
     const managers = useSelector((state) => state.subscriber.managers);
     const activeAccount = useSelector((state) => state.subscriber.activeAccount);
     const { numberOfUsers: usedUsers, packageNumberOfUsers: maxUsers } = activeAccount;
     const schedules = useSelector((state) => state.subscriber.scheduleSavedReport) || [];
     const [defaultPlaceholderTo, setDefaultPlaceholderTo] = useState('Select recipients');
     const [isLoading, setIsLoading] = useState(true);
     const [initialData, setInitialData] = useState({
          accountId: activeAccount.id,
          reportViewId: reportData.id,
          name: '',
          emailList: '',
          customSubject: '',
          optionalMessage: '',
          schedule: '',
          daysOfWeek: ['Sunday'],
          specificDay: '',
     });
     const [modalInviteUser, setModalInviteUser] = useState(false);
     const [privateLoading, setPrivateLoading] = useState(false);
     const [validateInviteEmail, setValidateInviteEmail] = useState('');
     const [newUserEmail, setNewUserEmail] = useState('');
     let emailsOfAccountUsers = [];

     if (owner) {
          emailsOfAccountUsers = [owner, ...managers].map((user) => ({ label: user.email, value: user.email }));
     }

     useEffect(() => {
          dispatch(fetchAccountUsers(activeAccount.id));
     }, []); // eslint-disable-line

     const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
     const scheduleOptions = [
          { value: 'daily', label: 'Daily' },
          { value: 'weekly', label: 'Weekly on' },
          { value: 'monthly', label: 'Monthly on' },
          { value: 'quarterly', label: 'Quarterly (calendar)' },
     ];

     const MenuList = (props) => {
          return (
               <components.MenuList {...props}>
                    {props.children}
               </components.MenuList>
          );
     };

     const noOptionsMessage = () => {
          return (
               <span>
                    No option.
               </span>
          );
     };

     useEffect(() => {
          if (showCreate.id) {
               setIsLoading(true);
               callTokenApi(`${CLIENT_SCHEDULE_SAVED_REPORT}/${showCreate.id}`, 'GET')
                    .then((response) => {
                         if (response.status === 200) {
                              setInitialData(response.data.schedule);
                         } else {
                              toastError(response);
                         }
                    })
                    .catch((error) => {
                         toastError(error);
                    })
                    .finally(() => setIsLoading(false));
          } else {
               setInitialData({
                    accountId: activeAccount.id,
                    reportViewId: reportData.id,
                    name: '',
                    emailList: '',
                    customSubject: '',
                    optionalMessage: '',
                    schedule: '',
                    daysOfWeek: ['Sunday'],
                    specificDay: '',
               });
               setIsLoading(false);
          }
     }, [activeAccount, showCreate, reportData]);

     const onSubmit = (values, { setSubmitting }) => {
          values.emailList = values.emailList.map((email) => email.value);
          if (values.schedule !== 'weekly') {
               values.daysOfWeek = [];
          }

          if (showCreate.id) {
               callCompressTokenApi(CLIENT_SCHEDULE_SAVED_REPORT, 'PUT', values)
                    .then((response) => {
                         if (response.status === 200) {
                              const { schedule } = response.data;
                              const newSchedules = schedules.map((item) => {
                                   if (item.id === schedule.id) {
                                        return { ...item, ...schedule };
                                   }
                                   return item;
                              });
                              dispatch(setScheduleSavedReport(newSchedules));
                              setSubmitting(false);
                              setShowCreate({ show: false });
                         } else {
                              toastError(response);
                         }
                    })
                    .finally(() => {});
          } else {
               callCompressTokenApi(CLIENT_SCHEDULE_SAVED_REPORT, 'POST', values).then((response) => {
                    if (response.status === 200) {
                         const updatedSavedReport = savedReports.map((report) => {
                              let totalSchedule = report.totalSchedule || 0;
                              if(report.id === reportData.id) {
                                   totalSchedule = totalSchedule + 1
                              }
                              return { ...report, totalSchedule }
                         })
                         dispatch(setScheduleSavedReport([response.data.schedule, ...schedules]));
                         dispatch(setSavedReport(updatedSavedReport));
                         setSubmitting(false);
                         setShowCreate({ show: false });
                    } else {
                         toastError(response);
                    }
               });
          }
     };

     const closeModalInviteUser = () => {
          if (usedUsers >= maxUsers && maxUsers !== -1) {
               dispatch(toggleAsideLimitationHit());
               dispatch(setAsideShow(true));
               return;
          }
          setModalInviteUser(false);
          setValidateInviteEmail('');
     };

     const handleNewUserEmail = (e) => {
          setNewUserEmail(e.target.value);
          setValidateInviteEmail('');
     };

     const toggleModalInviteUser = () => {
          if (usedUsers >= maxUsers && maxUsers !== -1) {
               dispatch(toggleAsideLimitationHit());
               dispatch(setAsideShow(true));
               return;
          }
          setModalInviteUser(!modalInviteUser);
          setValidateInviteEmail('');
     };

     const inviteUser = () => {
          if (usedUsers >= maxUsers && maxUsers !== -1) {
               toggleModalInviteUser();
               dispatch(toggleAsideLimitationHit());
               dispatch(setAsideShow(true));
               return;
          }

          let validateSchema = Yup.object().shape({
               newUserEmail: Yup.string()
                    .required('Email is required')
                    .email('Email invalid')
                    .test('checkSpecialCharacters', 'Email invalid', (value) => {
                         return value && /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(value);
                    }),
          });

          validateSchema
               .validate({ newUserEmail })
               .then(function () {
                    setValidateInviteEmail('');

                    if (activeAccount) {
                         const data = {
                              email: newUserEmail,
                              accountId: activeAccount.id,
                         };
                         setPrivateLoading(true);

                         callTokenApi(`${ADD_USER}`, 'POST', data).then((response) => {
                              setPrivateLoading(false);
                              if (response.status === 200) {
                                   if (response.data.useracc) {
                                        managers.push(response.data.user);
                                        dispatch(setManagers(managers));
                                   }
                                   toast.success(response.data.message);
                                   setNewUserEmail('');
                                   toggleModalInviteUser();
                              } else {
                                   if (response.data.accountBlocked) {
                                        dispatch(setShowBlockAccountPopup(true));
                                   } else {
                                        toastError(response);
                                   }
                                   toggleModalInviteUser();
                              }
                         });
                    }
               })
               .catch((error) => {
                    setValidateInviteEmail(error.message);
               });
     };

     const submitNewUserEmail = (e) => {
          if (e.keyCode === 13) {
               inviteUser();
          }
     };

     return (
          <div className='form-categories schedule-detail'>
               <Formik
                    enableReinitialize
                    initialValues={initialData}
                    validationSchema={() => validationSchema(schedules.filter((item) => item.id !== showCreate?.id))}
                    validateOnChange={false}
                    onSubmit={onSubmit}
                    validateOnBlur={false}
               >
                    {({ values, errors, setErrors, handleChange, handleSubmit, setFieldValue, isSubmitting, resetForm }) => {
                         const DELIVERY_SCHEDULE = {
                              monthly: 'Month',
                              quarterly: 'Quarter',
                         };

                         const optionFirstLast = [
                              { value: 'first', label: `First Day of the ${DELIVERY_SCHEDULE[values.schedule]}`, type: 'both' },
                              { value: 'last', label: `Last Day of the ${DELIVERY_SCHEDULE[values.schedule]}`, type: 'both' },
                              { value: 'firstMonday', label: `First Monday of Each ${DELIVERY_SCHEDULE[values.schedule]}`, type: 'monthly' },
                         ];

                         const handleClose = () => {
                              resetForm();
                              setShowCreate({ show: false });
                         };

                         return (
                              <CModal
                                   visible={showCreate.show}
                                   alignment='center'
                                   onClose={handleClose}
                                   color='primary'
                                   portal={false}
                                   backdrop='static'
                              >
                                   {isLoading ? (
                                        <CSpinner color='primary' />
                                   ) : (
                                        <CForm onSubmit={handleSubmit} noValidate>
                                             <CCard className='card form-categories--card form-categories--categorizing'>
                                                  <CIcon icon='cil-x' onClick={handleClose} className='icon-close-popup' />
                                                  <CModalBody>
                                                       <div className='popup-publish-version-description'>
                                                            <h3>{showCreate.id ? 'Edit' : 'Create'} a Scheduled Delivery</h3>
                                                            {reportData?.name && (
                                                                 <p>
                                                                      You are {showCreate.id ? 'editing' : 'creating'} a scheduled delivery for the
                                                                      report named <strong>{reportData?.name}</strong>
                                                                 </p>
                                                            )}
                                                       </div>
                                                       <CRow>
                                                            <CCol lg='12'>
                                                                 <CFormGroup>
                                                                      <CLabel htmlFor='name'>
                                                                           {' '}
                                                                           <strong>Schedule Name: </strong>
                                                                      </CLabel>
                                                                      <CInput
                                                                           id='name'
                                                                           name='name'
                                                                           placeholder='Schedule Name (only used to manage schedules)'
                                                                           invalid={!!errors.name}
                                                                           required
                                                                           value={values.name}
                                                                           onChange={(e) => {
                                                                                setFieldValue('name', e.target.value);
                                                                                if (errors) {
                                                                                     setErrors({ ...errors, name: '' });
                                                                                }
                                                                           }}
                                                                      />
                                                                      <CInvalidFeedback>{errors.name}</CInvalidFeedback>
                                                                 </CFormGroup>
                                                            </CCol>
                                                            <CCol lg='12'>
                                                                 <CFormGroup>
                                                                      <CLabel htmlFor='emailList'>
                                                                           <div className='title'>
                                                                                <span>
                                                                                     <strong>Select Recipients: </strong>
                                                                                </span>
                                                                                Recipients must be users inside your ListenLayer account.
                                                                           </div>
                                                                      </CLabel>
                                                                      <Select
                                                                           id='emailList'
                                                                           classNamePrefix="react-select"
                                                                           isMulti
                                                                           name='emailList'
                                                                           options={emailsOfAccountUsers}
                                                                           noOptionsMessage={noOptionsMessage}
                                                                           value={values.emailList}
                                                                           onChange={(emails) => {
                                                                                setFieldValue('emailList', emails || []);
                                                                                if (errors) {
                                                                                     setErrors({ ...errors, emailList: '' });
                                                                                }
                                                                           }}
                                                                           placeholder='Select recipients'
                                                                           className={errors.emailList ? 'is-invalid' : ''}
                                                                           components={{ MenuList }}
                                                                           isSearchable={defaultPlaceholderTo === 'Select recipients' ? false : true}
                                                                           onMenuOpen={() => setDefaultPlaceholderTo('Type here to filter the list')}
                                                                      />
                                                                      <CInvalidFeedback>{errors.emailList}</CInvalidFeedback>
                                                                      <div className='btn-invite-user'>
                                                                           <CLink onClick={toggleModalInviteUser}>Invite new users.</CLink>
                                                                      </div>
                                                                 </CFormGroup>
                                                            </CCol>
                                                            <CCol lg='12'>
                                                                 <CFormGroup>
                                                                      <CLabel htmlFor='customSubject'>
                                                                           <div className='title'>
                                                                                <span>
                                                                                     <strong>Custom Message: </strong>
                                                                                </span>
                                                                                Include a custom subject line for the delivered email, as well as an
                                                                                optional message.
                                                                           </div>
                                                                      </CLabel>
                                                                      <CInput
                                                                           id='customSubject'
                                                                           name='customSubject'
                                                                           placeholder='Custom Subject'
                                                                           invalid={!!errors.customSubject}
                                                                           value={values.customSubject}
                                                                           onChange={(e) => {
                                                                                setFieldValue('customSubject', e.target.value);
                                                                                if (errors) {
                                                                                     setErrors({ ...errors, customSubject: '' });
                                                                                }
                                                                           }}
                                                                      />
                                                                      <CInvalidFeedback>{errors.customSubject}</CInvalidFeedback>
                                                                 </CFormGroup>
                                                            </CCol>
                                                            <CCol lg='12'>
                                                                 <CFormGroup>
                                                                      <CTextarea
                                                                           id='optionalMessage'
                                                                           name='optionalMessage'
                                                                           placeholder='Optional message'
                                                                           className={`form-control ${errors.optionalMessage ? 'is-invalid' : ''}`}
                                                                           value={values.optionalMessage}
                                                                           onChange={handleChange}
                                                                           rows={5}
                                                                      />
                                                                 </CFormGroup>
                                                            </CCol>
                                                            <CCol lg='12'>
                                                                 <CFormGroup>
                                                                      <CLabel htmlFor='schedule'>
                                                                           <div className='title'>
                                                                                <span>
                                                                                     <strong>Delivery Schedule: </strong>
                                                                                </span>
                                                                                Choose the cadence you'd like this report to be delivered on.
                                                                           </div>
                                                                      </CLabel>
                                                                      <Select
                                                                           id='schedule'
                                                                           name='schedule'
                                                                           placeholder='Select a schedule'
                                                                           className={`react-select ${errors.schedule ? 'is-invalid' : ''}`}
                                                                           options={scheduleOptions}
                                                                           value={scheduleOptions.find((option) => option.value === values.schedule)}
                                                                           onChange={(e) => {
                                                                                setFieldValue('schedule', e.value || '');
                                                                                if (
                                                                                     e.value === 'weekly' &&
                                                                                     (!values.daysOfWeek || values.daysOfWeek.length === 0)
                                                                                ) {
                                                                                     setFieldValue('daysOfWeek', ['Sunday']);
                                                                                }
                                                                                if (errors) {
                                                                                     setErrors({ ...errors, schedule: '' });
                                                                                }
                                                                           }}
                                                                      />
                                                                      <CInvalidFeedback>{errors.schedule}</CInvalidFeedback>
                                                                 </CFormGroup>
                                                            </CCol>
                                                            {(values.schedule === 'monthly' || values.schedule === 'quarterly') && (
                                                                 <CCol lg='12'>
                                                                      <CFormGroup>
                                                                           <Select
                                                                                id='specificDay'
                                                                                name='specificDay'
                                                                                placeholder='Select an option'
                                                                                className={`react-select ${errors.specificDay ? 'is-invalid' : ''}`}
                                                                                options={optionFirstLast.filter((option) => {
                                                                                     if (values.schedule === 'monthly') {
                                                                                          return true;
                                                                                     } else {
                                                                                          return option.type === 'both';
                                                                                     }
                                                                                })}
                                                                                value={optionFirstLast.find(
                                                                                     (option) => option.value === values.specificDay
                                                                                )}
                                                                                onChange={(e) => {
                                                                                     setFieldValue('specificDay', e.value || '');
                                                                                     if (errors) {
                                                                                          setErrors({ ...errors, specificDay: '' });
                                                                                     }
                                                                                }}
                                                                           />
                                                                           <CInvalidFeedback>{errors.specificDay}</CInvalidFeedback>
                                                                      </CFormGroup>
                                                                 </CCol>
                                                            )}
                                                            {values.schedule === 'weekly' && (
                                                                 <CCol lg='12'>
                                                                      <CFormGroup>
                                                                           <div style={{ display: 'flex', gap: '10px', justifyContent: 'center' }}>
                                                                                {days.map((day, index) => {
                                                                                     const isShowWarn =
                                                                                          values.daysOfWeek.includes(day) &&
                                                                                          values.daysOfWeek.length === 1;
                                                                                     return (
                                                                                          <CButton
                                                                                               key={index}
                                                                                               onClick={() => {
                                                                                                    const isChecked =
                                                                                                         values.daysOfWeek.includes(day) || false;
                                                                                                    const updatedDays = isChecked
                                                                                                         ? values.daysOfWeek.filter((d) => d !== day)
                                                                                                         : [...values.daysOfWeek, day];
                                                                                                    !isShowWarn
                                                                                                         ? setFieldValue('daysOfWeek', updatedDays)
                                                                                                         : toast.error('Select at least 1 option!');
                                                                                               }}
                                                                                               className={`day-of-week ${values.daysOfWeek.includes(day) ? `checked` : ''}`}
                                                                                          >
                                                                                               {day.slice(0, 1)}
                                                                                          </CButton>
                                                                                     );
                                                                                })}
                                                                           </div>
                                                                      </CFormGroup>
                                                                 </CCol>
                                                            )}
                                                       </CRow>
                                                       <div className='form-actions text-center'>
                                                            <CButton type='submit' color='primary' disabled={isSubmitting} className='mr-3'>
                                                                 {isSubmitting ? 'Waiting' : 'Save'}
                                                            </CButton>
                                                            <CButton className='btn btn-light' onClick={handleClose}>
                                                                 Cancel
                                                            </CButton>
                                                       </div>
                                                       <div></div>
                                                  </CModalBody>
                                             </CCard>
                                        </CForm>
                                   )}
                              </CModal>
                         );
                    }}
               </Formik>
               <InviteUserPopup
                    show={modalInviteUser}
                    isLoading={privateLoading}
                    onClose={closeModalInviteUser}
                    event={inviteUser}
                    btnText={'Add User'}
               >
                    <h2>Invite New User</h2>
                    <p>
                         Add a new user to {activeAccount.name}. If they already have a ListenLayer login, we'll automatically grant them access and
                         send them an email notifucation. If they do not yet have a ListenLayer account, we will invite them to create one.
                    </p>
                    <div className='invite-user-form'>
                         <CLabel>New User's Email:</CLabel>
                         <CInput
                              value={newUserEmail}
                              onChange={handleNewUserEmail}
                              onKeyDown={submitNewUserEmail}
                              data-lpignore='true'
                              invalid={!!validateInviteEmail}
                         />
                         <CInvalidFeedback>{validateInviteEmail}</CInvalidFeedback>
                    </div>
               </InviteUserPopup>
          </div>
     );
};

export default CreateSchedule;
